import { kickHeights } from "@Context/game/builder/builder-context";

const GRAVITY = 9.81;

interface KickHeight {
  height: number;
  name: string;
  min: number;
  max: number;
}

const getProjectileFlightTime = (height: number): number | null => {
  if (height <= 0) return null;
  return 2 * Math.sqrt((2 * height) / GRAVITY);
};

const getHeightFromFlightTime = (flightTime: number): number => {
  if (flightTime <= 0) return 0;
  const halfTime = flightTime / 2;
  return (GRAVITY * halfTime * halfTime) / 2;
};

const getNearestKickHeight = (height: number): KickHeight => {
  let nearest = kickHeights[0];
  let minDiff = Math.abs(height - nearest.height);
  for (let i = 1; i < kickHeights.length; i++) {
    const diff = Math.abs(height - kickHeights[i].height);
    if (diff < minDiff) {
      minDiff = diff;
      nearest = kickHeights[i];
    }
  }
  return nearest;
};

const getCategoryForHeight = (height: number): KickHeight => {
  if (height <= 0) return kickHeights[0];
  for (let i = 1; i < kickHeights.length; i++) {
    const cat = kickHeights[i];
    if (height >= cat.min && height < cat.max) return cat;
  }
  const last = kickHeights[kickHeights.length - 1];
  if (height >= last.min) return last;
  return getNearestKickHeight(height);
};

interface BestHeightResult {
  height: number;
  needsTimeAdjust: boolean;
  clampedTo: number | null;
}

const getBestHeightForCategory = (
  category: KickHeight,
  flightTime: number
): BestHeightResult => {
  if (category.height <= 0) {
    return { height: 0, needsTimeAdjust: false, clampedTo: null };
  }

  const naturalHeight = getHeightFromFlightTime(flightTime);

  if (naturalHeight >= category.min && naturalHeight <= category.max) {
    return { height: naturalHeight, needsTimeAdjust: false, clampedTo: null };
  }

  if (naturalHeight < category.min) {
    return { height: category.min, needsTimeAdjust: true, clampedTo: category.min };
  }

  return { height: category.max, needsTimeAdjust: true, clampedTo: category.max };
};

const isHeightInCategoryRange = (height: number, flightTime: number): boolean => {
  if (height <= 0) return true;
  const category = getCategoryForHeight(height);
  if (category.height <= 0) return true;
  const naturalHeight = getHeightFromFlightTime(flightTime);
  return naturalHeight >= category.min && naturalHeight <= category.max;
};

export {
  getBestHeightForCategory,
  getCategoryForHeight,
  getHeightFromFlightTime,
  getNearestKickHeight,
  getProjectileFlightTime,
  isHeightInCategoryRange,
};

export type { BestHeightResult, KickHeight };
